import { useDispatch, useSelector } from 'react-redux';
import { useCallback, useMemo } from 'react';
import { createSelector } from 'reselect';
import { emptyArray, emptyObject } from 'ui-components/default-props/empty';

import Global from '../../../store/global-selectors';
import { sortByPropAsc } from '../../../services/sort-service';
import { filterDutiesOptions } from '../../../fulfillment-planner/duties-and-taxes/helpers';
import * as EntitiesActions from '../../../store/entities/entities.actions';

export const useEntitiesFulfillmentPlan = (fulfillmentPlanId) =>
  useSelector((state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }));

export const useFulfillmentRestrictionsByFPId = (fulfillmentPlanId) => {
  const restrictions = useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'restrictions',
    }),
  );

  return restrictions;
};

export const useEntitiesFulfillmentPlansFromFulfillmentPlanIds = (fulfillmentPlanIds) =>
  useSelector((state) => Global.selectEntitiesFulfillmentPlansByIds(state, { fulfillmentPlanIds }));

export const useEntitiesFulfillmentPlansFromSalesOrderIds = (salesOrderIds) =>
  useSelector((state) =>
    Global.selectEntitiesFulfillmentPlansBySalesOrderIds(state, {
      salesOrderIds,
    }),
  );

export const useEntitiesFulfillmentPlansFromSalesOrderId = (salesOrderId) => useEntitiesFulfillmentPlansFromSalesOrderIds([salesOrderId]);

export const useEntitiesFulfillmentPlanProperty = (fulfillmentPlanId, propertyName) =>
  useSelector((state) =>
    Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: propertyName,
    }),
  );

export const useEntitiesFulfillmentPlanSetFromFulfillmentPlanId = (fulfillmentPlanId) => {
  const batch = useSelector((state) => {
    const fulfillmentPlanSetId = Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId,
      property: 'fulfillmentPlanSetId',
    });
    return Global.selectEntitiesFulfillmentPlanSetById(state, {
      fulfillmentPlanSetId: fulfillmentPlanSetId || 0,
    });
  });
  return {
    id: batch?.fulfillmentPlanSetId.toString(),
    name: batch?.fulfillmentPlanSetName,
  };
};

const addCounterToFulfillmentPlans = (fulfillmentPlans) => {
  return fulfillmentPlans.sort(sortByPropAsc('createDateTime')).map((x, index) => ({
    ...x,
    counter: index + 1,
  }));
};

const addCounterToFulfillmentPlanFactory = () =>
  createSelector(
    (salesOrderId, state) =>
      Global.selectEntitiesFulfillmentPlansBySalesOrderIds(state, {
        salesOrderIds: [salesOrderId],
      }),
    (fulfillmentPlans) => addCounterToFulfillmentPlans(fulfillmentPlans || emptyArray),
  );

export const useAddCounterToFulfillmentPlans = (salesOrderId, fulfillmentPlanId) => {
  const orderNumber = useSelector((state) =>
    Global.selectEntitiesSalesOrderProperty(state, {
      salesOrderId,
      property: 'orderNumber',
    }),
  );
  const addCounterSelector = useMemo(addCounterToFulfillmentPlanFactory, []);
  const sortedFulfillmentPlansWithCounter = useSelector((state) => addCounterSelector(salesOrderId, state)) || emptyArray;
  const { counter = 0 } = sortedFulfillmentPlansWithCounter.find((fp) => fp.fulfillmentPlanId === fulfillmentPlanId) || emptyObject;
  return {
    fulfillmentPlanId,
    counter,
    total: sortedFulfillmentPlansWithCounter.length,
    orderNumber,
  };
};

export const useGetFulfillmentPlanIdsFromSalesOrderIdWithEligibleStatus = (salesOrderId, eligibleStatuses = []) => {
  return useSelector((state) => {
    const fulfillmentPlans = Global.selectEntitiesFulfillmentPlansBySalesOrderIds(state, {
      salesOrderIds: [salesOrderId],
    });
    if (eligibleStatuses.length === 0) {
      return fulfillmentPlans.map(({ fulfillmentPlanId }) => fulfillmentPlanId);
    }

    return fulfillmentPlans
      .filter(({ orderGridStatus }) => eligibleStatuses.includes(orderGridStatus))
      .map(({ fulfillmentPlanId }) => fulfillmentPlanId);
  });
};

export const useGetFulfillmentPlansFromSalesOrderIdWithoutStatus = (salesOrderId, inEligibleStatuses = []) => {
  const salesOrderFulfillmantPlans = useEntitiesFulfillmentPlansFromSalesOrderId(salesOrderId);
  const fulfillmantPlans = useMemo(() => {
    if (inEligibleStatuses.length === 0) {
      return salesOrderFulfillmantPlans;
    }

    return salesOrderFulfillmantPlans.filter(({ orderGridStatus }) => !inEligibleStatuses.includes(orderGridStatus));
  }, [salesOrderFulfillmantPlans, inEligibleStatuses]);

  return fulfillmantPlans;
};

const makeRestoreSelectorFactory = (fulfillmentPlanId) =>
  createSelector(
    (state) => Global.selectEntitiesFulfillmentPlanById(state, { fulfillmentPlanId }),
    (state) =>
      Global.selectEntitiesFulfillmentsByFulfillmentPlanId(state, {
        fulfillmentPlanId,
      }),
    (fulfillmentPlan, fulfillments) => {
      const activeFulfillmentIds = fulfillments
        .filter((fulfillment) => !fulfillment?.isLabelVoided && !fulfillment?.externalFulfillment?.isVoided)
        .map((fulfillment) => fulfillment.fulfillmentId);
      const hasActiveFulfillments = activeFulfillmentIds.length > 0;

      return {
        hasActiveFulfillments,
        orderGridStatus: fulfillmentPlan?.orderGridStatus,
      };
    },
  );

export const useGetFulfillmentPlanDataForRestore = (fulfillmentPlanId) => {
  const makeRestoreSelector = useMemo(() => makeRestoreSelectorFactory(fulfillmentPlanId), [fulfillmentPlanId]);
  return useSelector(makeRestoreSelector);
};

export const usePrepayDutiesDataFromEntities = (fulfillmentPlanIds) => {
  const availableShippingOptions = useSelector((state) => {
    return Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId: fulfillmentPlanIds[0],
      property: 'availableShippingOptions',
    });
  });
  const availableDutiesOptions = useMemo(() => {
    return filterDutiesOptions(availableShippingOptions);
  }, [availableShippingOptions]);

  const otherShippingOptions = useSelector((state) => {
    return Global.selectEntitiesFulfillmentPlanProperty(state, {
      fulfillmentPlanId: fulfillmentPlanIds[0],
      property: 'labelConfiguration.options',
    });
  });

  const dispatch = useDispatch();
  const onSelectedDutyOptionChange = useCallback(
    (fulfillmentPlanId, availableOption) => {
      dispatch(EntitiesActions.updateFulfillmentPlanAdditionalOptions(fulfillmentPlanId, [availableOption]));
    },
    [dispatch],
  );

  return {
    availableDutiesOptions,
    otherShippingOptions,
    onSelectedDutyOptionChange,
  };
};
